import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "~/lib/utils"

const cardVariants = cva("h-card", {
  variants: {
    variant: {
      default: "",
      ghost: "h-card--ghost",
      signal: "h-card--signal",
    },
    density: {
      compact: "h-card--compact",
      default: "",
      relaxed: "h-card--relaxed",
    },
    isLink: {
      true: "h-card--link",
    },
  },
  defaultVariants: {
    variant: "default",
    density: "default",
  },
})

interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  outerTitle?: React.ReactNode
  outerTitleClassName?: string
  grow?: boolean
  href?: string
  linkAriaLabel?: string
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      outerTitle,
      outerTitleClassName,
      grow,
      variant,
      density,
      isLink,
      href,
      linkAriaLabel,
      children,
      ...props
    },
    ref,
  ) => (
    <div className={cn("h-card-wrapper", className)}>
      {outerTitle && (
        <div className={cn("w-full mb-4", outerTitleClassName)}>
          {outerTitle}
        </div>
      )}
      <div
        ref={ref}
        className={cn(cardVariants({ variant, density, isLink }))}
        style={grow ? { height: "100%" } : {}}
        {...props}
      >
        {isLink && href && (
          <a href={href} className="h-card__link">
            <span className="sr-only">{linkAriaLabel || "Open"}</span>
          </a>
        )}
        {children}
      </div>
    </div>
  ),
)
Card.displayName = "Card"

interface CardTitleProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardTitle = React.forwardRef<HTMLDivElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => {
    const actions = React.Children.toArray(children).find(
      (child) => React.isValidElement(child) && child.type === CardTitleActions,
    )
    const titleContent = React.Children.toArray(children).filter(
      (child) =>
        !React.isValidElement(child) || child.type !== CardTitleActions,
    )

    return (
      <div ref={ref} className={cn("h-card__title", className)} {...props}>
        <div>{titleContent}</div>
        {actions}
      </div>
    )
  },
)
CardTitle.displayName = "CardTitle"

interface CardTitleSuffixProps extends React.HTMLAttributes<HTMLSpanElement> {}

const CardTitleSuffix = React.forwardRef<HTMLSpanElement, CardTitleSuffixProps>(
  ({ className, children, ...props }, ref) => (
    <span
      ref={ref}
      className={cn("h-card__title-suffix", className)}
      {...props}
    >
      {" "}
      <span className="h-card__title-suffix-separator">·</span> {children}
    </span>
  ),
)
CardTitleSuffix.displayName = "CardTitleSuffix"

interface CardTitleActionsProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardTitleActions = React.forwardRef<
  HTMLDivElement,
  CardTitleActionsProps
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("h-card__title-actions", className)} {...props}>
    {children}
  </div>
))
CardTitleActions.displayName = "CardTitleActions"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("h-card-content", className)} {...props} />
))
CardContent.displayName = "CardContent"

export { Card, CardTitle, CardTitleSuffix, CardTitleActions, CardContent }
